// Navigation filtrée selon les modules effectifs et les droits.
import {
  ShieldCheck,
  CalendarDays,
  Newspaper,
  FileSignature,
  Building,
  CalendarRange,
  Contact,
  Globe,
  HandCoins,
  ChartNoAxesColumn,
  Eye,
  Image,
  LayoutDashboard,
  Lock,
  Mail,
  Receipt,
  Settings,
  ShoppingBag,
  Truck,
  ScanLine,
  Users,
  Frame,
  Wallet,
  Landmark,
  CreditCard,
  Sparkles,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { moduleDeLaNavigation, type Action, type Module } from '@artosera/shared';
import { galerieALaction, galerieVoitModule, moduleEnLectureSeule } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';

/**
 * Les onze entrées de la barre latérale, dans l'ordre de 3.2, plus « Groupe »
 * qui n'apparaît que pour un rôle de groupe (posé séparément, `entreeGroupe`).
 *
 * Le MODULE d'une entrée ne se déclare pas ici : il vient du registre de
 * `@artosera/shared` (`moduleDeLaNavigation`, D21), le même qui garde l'API.
 * Une entrée d'un module inactif est masquée ; son URL directe mène à la page
 * « Module non activé » (`RegieShell`). Un module en lecture seule garde son
 * entrée, marquée d'un cadenas.
 */
export interface EntreeNavigation {
  readonly cle: string;
  readonly chemin: string;
  readonly icone: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  readonly libelleNavigation: string;
  readonly action: Action;
  /**
   * Le module qui montre l'entrée, quand sa page est au cœur sans être pour
   * tous : « Site » mène à `demande-site` (route du cœur), mais n'apparaît
   * qu'à la galerie qui a acheté le service (décision du 27 octobre 2026).
   */
  readonly module?: Module;
}

export const ENTREES_NAVIGATION: readonly EntreeNavigation[] = [
  {
    cle: 'demander',
    chemin: 'demander',
    icone: Sparkles,
    libelleNavigation: 'commun:navigation.demander',
    action: 'recherche:ask',
  },
  {
    cle: 'signatures',
    chemin: 'signatures',
    icone: FileSignature,
    libelleNavigation: 'commun:navigation.signatures',
    action: 'signatures:read',
  },
  {
    cle: 'tableauDeBord',
    chemin: '',
    icone: LayoutDashboard,
    libelleNavigation: 'commun:navigation.tableauDeBord',
    action: 'dashboard:read',
  },
  {
    cle: 'oeuvres',
    chemin: 'oeuvres',
    icone: Image,
    libelleNavigation: 'commun:navigation.oeuvres',
    action: 'artworks:read',
  },
  // catalogage
  {
    cle: 'catalogage',
    chemin: 'oeuvres/catalogage',
    icone: Image,
    libelleNavigation: 'regie:catalogage.titre',
    action: 'artworks:read',
  },
  {
    cle: 'artistes',
    chemin: 'artistes',
    icone: Users,
    libelleNavigation: 'commun:navigation.artistes',
    action: 'artists:read',
  },
  {
    cle: 'tresorerie',
    chemin: 'tresorerie',
    icone: Landmark,
    libelleNavigation: 'commun:navigation.tresorerie',
    action: 'treasury:read',
  },
  {
    cle: 'comptes-artistes',
    chemin: 'comptes-artistes',
    icone: Wallet,
    libelleNavigation: 'commun:navigation.comptesArtistes',
    action: 'artist-accounts:read',
  },
  {
    cle: 'pilotage',
    chemin: 'pilotage',
    icone: ChartNoAxesColumn,
    libelleNavigation: 'commun:modules.pilotage.libelle',
    action: 'artworks:read',
  },
  {
    cle: 'expositions',
    chemin: 'expositions',
    icone: CalendarRange,
    libelleNavigation: 'commun:navigation.expositions',
    action: 'exhibitions:read',
  },
  // Studio.
  {
    cle: 'studio',
    chemin: 'studio',
    icone: FileSignature,
    libelleNavigation: 'regie:studio.titre',
    action: 'studio:read',
  },
  {
    cle: 'evenements',
    chemin: 'evenements',
    icone: CalendarDays,
    libelleNavigation: 'commun:navigation.evenements',
    action: 'events:read',
  },
  {
    cle: 'agenda',
    chemin: 'agenda',
    icone: CalendarDays,
    libelleNavigation: 'commun:navigation.agenda',
    action: 'agenda:read',
  },
  {
    cle: 'rendez-vous-en-ligne',
    chemin: 'rendez-vous-en-ligne',
    icone: CalendarDays,
    libelleNavigation: 'regie:priseRendezVous.titre',
    action: 'creneaux:read',
  },
  {
    cle: 'presse',
    chemin: 'presse',
    icone: Newspaper,
    libelleNavigation: 'commun:navigation.presse',
    action: 'press:read',
  },
  {
    cle: 'contacts',
    chemin: 'contacts',
    icone: Contact,
    libelleNavigation: 'commun:navigation.contacts',
    action: 'contacts:read',
  },
  {
    cle: 'espaces-collectionneurs',
    chemin: 'espaces-collectionneurs',
    icone: Contact,
    libelleNavigation: 'regie:espaceCollectionneur.espaces',
    action: 'collector-space:read',
  },
  {
    cle: 'ventes',
    chemin: 'ventes',
    icone: Receipt,
    libelleNavigation: 'commun:navigation.ventes',
    action: 'reservations:read',
  },
  {
    cle: 'offres',
    chemin: 'offres',
    icone: HandCoins,
    libelleNavigation: 'commun:navigation.offres',
    action: 'offers:read',
  },
  {
    cle: 'viewingRooms',
    chemin: 'viewing-rooms',
    icone: Eye,
    libelleNavigation: 'commun:navigation.viewingRooms',
    action: 'viewing-rooms:read',
  },
  {
    cle: 'paiement-en-ligne',
    chemin: 'paiement-en-ligne',
    icone: CreditCard,
    libelleNavigation: 'commun:navigation.paiementEnLigne',
    action: 'online-payments:read',
  },
  {
    cle: 'boite-reception',
    chemin: 'boite-reception',
    icone: Mail,
    libelleNavigation: 'regie:boiteReception.titre',
    action: 'inbox:read',
  },
  {
    cle: 'boite-reception/assistant',
    chemin: 'boite-reception/assistant',
    icone: Mail,
    libelleNavigation: 'regie:assistantReponse.assistant',
    action: 'inbox:settings',
  },
  {
    cle: 'boite-reception/delais',
    chemin: 'boite-reception/delais',
    icone: ChartNoAxesColumn,
    libelleNavigation: 'regie:boiteReception.delais',
    action: 'inbox:settings',
  },
  {
    cle: 'pipeline',
    chemin: 'pipeline',
    icone: ChartNoAxesColumn,
    libelleNavigation: 'regie:pipeline.titre',
    action: 'pipeline:read',
  },
  {
    cle: 'presentation',
    chemin: 'presentation',
    icone: Eye,
    libelleNavigation: 'presentation:titre',
    action: 'presentation:read',
  },
  {
    cle: 'locations',
    chemin: 'locations',
    icone: CalendarRange,
    libelleNavigation: 'commun:navigation.locations',
    action: 'locations:read',
  },
  {
    cle: 'mouvements',
    chemin: 'mouvements',
    icone: Truck,
    libelleNavigation: 'commun:navigation.mouvements',
    action: 'movements:read',
  },
  {
    cle: 'inventaire',
    chemin: 'inventaire',
    icone: ScanLine,
    libelleNavigation: 'regie:inventaire.titre',
    action: 'inventaire:read',
  },
  {
    cle: 'depots-recus',
    chemin: 'depots-recus',
    icone: Truck,
    libelleNavigation: 'commun:navigation.depots-recus',
    action: 'movements:read',
  },
  {
    cle: 'accrochage',
    chemin: 'accrochage',
    icone: Frame,
    libelleNavigation: 'commun:navigation.accrochage',
    action: 'exhibitions:read',
  },
  {
    cle: 'assurance',
    chemin: 'assurance',
    icone: ShieldCheck,
    libelleNavigation: 'commun:navigation.assurance',
    action: 'insurance:read',
  },
  {
    cle: 'coffre',
    chemin: 'coffre',
    icone: Lock,
    libelleNavigation: 'commun:navigation.coffre',
    action: 'documents:read',
  },
  {
    cle: 'contrats',
    chemin: 'contrats',
    icone: FileSignature,
    libelleNavigation: 'commun:navigation.contrats',
    action: 'contracts:read',
  },
  {
    cle: 'site',
    chemin: 'demande-site',
    icone: Globe,
    libelleNavigation: 'commun:navigation.site',
    action: 'site:read',
    module: 'site',
  },
  {
    cle: 'boutique',
    chemin: 'boutique',
    icone: ShoppingBag,
    libelleNavigation: 'commun:navigation.boutique',
    action: 'orders:read',
  },
  {
    cle: 'newsletter',
    chemin: 'newsletter',
    icone: Mail,
    libelleNavigation: 'commun:navigation.newsletter',
    action: 'newsletter:read',
  },
  {
    cle: 'registre-police',
    chemin: 'registre-police',
    icone: FileSignature,
    libelleNavigation: 'commun:navigation.registre-police',
    action: 'registre-police:read',
  },
  {
    cle: 'reglages',
    chemin: 'reglages',
    icone: Settings,
    libelleNavigation: 'commun:navigation.reglages',
    action: 'settings:read',
  },
];

/** « Groupe » (3.2) : sous le sélecteur de galerie, visible pour un rôle de groupe seulement. */
export const ENTREE_GROUPE = {
  cle: 'groupe',
  chemin: 'groupe',
  icone: Building,
  libelleNavigation: 'commun:navigation.groupe',
} as const;

/** Le module d'une entrée, lu dans le registre partagé (D21). */
export function moduleDeLEntree(
  entree: Pick<EntreeNavigation, 'chemin' | 'module'>,
): Module | null {
  return entree.module ?? moduleDeLaNavigation(entree.chemin);
}

/**
 * Les entrées que cette galerie ouvre réellement : masquées par
 * `galerieALaction` (5.4, la liste `actions` de l'API fait foi) et par
 * `galerieVoitModule` (D21, les modules effectifs que l'API rend, plus ceux
 * qu'elle rend en lecture seule).
 * Utilisé par la barre latérale, le menu mobile et la palette ⌘K, pour ne
 * jamais proposer un lien que l'API refuserait.
 */
export function entreesVisibles(galerie: GalerieSession): readonly EntreeNavigation[] {
  return ENTREES_NAVIGATION.filter((entree) => {
    if (
      !galerieALaction(galerie, entree.action) &&
      !(entree.cle === 'reglages' && galerieALaction(galerie, 'addons:invoices'))
    )
      return false;
    const module = moduleDeLEntree(entree);
    if (module !== null && !galerieVoitModule(galerie, module)) return false;
    return true;
  });
}

/**
 * Cette entrée mène-t-elle à un module consultable en lecture seule ? Elle
 * garde sa place, avec un cadenas : « tout voir, rien créer ».
 */
export function entreeEnLectureSeule(
  galerie: GalerieSession,
  entree: Pick<EntreeNavigation, 'chemin' | 'module'>,
): boolean {
  const module = moduleDeLEntree(entree);
  return module !== null && moduleEnLectureSeule(galerie, module);
}
